import { Select } from "./Select";
import { TextInput } from "./TextInput";

const CREDENTIAL_TYPE_LABELS: Record<string, string> = {
  UsernamePassword: "Username & password",
  UsernameKeyPair: "Username & key pair",
  DomainUsernamePassword: "Domain, username & password",
  BearerToken: "Bearer token",
  AwsAccessKey: "AWS access key",
  AwsIamRole: "AWS identity",
  AzureServicePrincipal: "Azure service principal",
  AzureManagedIdentity: "Azure identity",
  GoogleServiceAccount: "Google Cloud service account",
  GoogleIdentity: "Google Cloud identity",
};

const PASSWORD_CREDENTIAL_TYPES = [
  "UsernamePassword",
  "DomainUsernamePassword",
  "AwsAccessKey",
  "AzureServicePrincipal",
];

export interface CredentialSummary {
  credentialType: string;
  domain: string | null;
  username: string | null;
  hasPassword: boolean;
  hasPrivateKey: boolean;
  hasPassphrase: boolean;
  updatedAt: string | null;
}

interface CredentialFieldsProps {
  credentialTypeOptions: string[];
  credentialType: string;
  onCredentialTypeChange: (value: string) => void;
  domain: string;
  onDomainChange: (value: string) => void;
  username: string;
  onUsernameChange: (value: string) => void;
  password: string;
  onPasswordChange: (value: string) => void;
  privateKey: string;
  onPrivateKeyChange: (value: string) => void;
  passphrase: string;
  onPassphraseChange: (value: string) => void;
  editingCredential: CredentialSummary | null;
}

export function CredentialFields({
  credentialTypeOptions,
  credentialType,
  onCredentialTypeChange,
  domain,
  onDomainChange,
  username,
  onUsernameChange,
  password,
  onPasswordChange,
  privateKey,
  onPrivateKeyChange,
  passphrase,
  onPassphraseChange,
  editingCredential,
}: CredentialFieldsProps) {
  if (credentialTypeOptions.length === 0) {
    return null;
  }

  const isAwsAccessKey = credentialType === "AwsAccessKey";
  const isAzureServicePrincipal = credentialType === "AzureServicePrincipal";

  return (
    <>
      <div>
        <label
          htmlFor="resourceCredentialType"
          className="mb-1.5 block text-sm text-foreground"
        >
          Credential
        </label>
        <Select
          id="resourceCredentialType"
          value={credentialType}
          onChange={(e) => onCredentialTypeChange(e.target.value)}
          className="w-full"
        >
          {credentialTypeOptions.map((c) => (
            <option key={c} value={c}>
              {CREDENTIAL_TYPE_LABELS[c] ?? c}
            </option>
          ))}
        </Select>
      </div>
      {(credentialType === "DomainUsernamePassword" ||
        isAzureServicePrincipal) && (
        <div className="w-1/2">
          <label
            htmlFor="resourceCredentialDomain"
            className="mb-1.5 block text-sm text-foreground"
          >
            {isAzureServicePrincipal ? "Tenant ID" : "Domain"}
          </label>
          <TextInput
            id="resourceCredentialDomain"
            value={domain}
            onChange={(e) => onDomainChange(e.target.value)}
            placeholder={isAzureServicePrincipal ? undefined : "CONTOSO"}
            autoComplete="off"
            className="w-full"
          />
        </div>
      )}
      {PASSWORD_CREDENTIAL_TYPES.includes(credentialType) && (
        <div className="flex items-end gap-3">
          <div className="min-w-0 basis-1/2">
            <label
              htmlFor="credentialUsername"
              className="mb-1.5 block text-sm text-foreground"
            >
              {isAwsAccessKey
                ? "Access key ID"
                : isAzureServicePrincipal
                  ? "Client ID"
                  : "Username"}
            </label>
            <TextInput
              id="credentialUsername"
              value={username}
              onChange={(e) => onUsernameChange(e.target.value)}
              required
              className="w-full"
            />
          </div>
          <div className="min-w-0 basis-1/2">
            <label
              htmlFor="credentialPassword"
              className="mb-1.5 block text-sm text-foreground"
            >
              {isAwsAccessKey
                ? "Secret access key"
                : isAzureServicePrincipal
                  ? "Client secret"
                  : "Password"}
              {editingCredential?.hasPassword
                ? " (leave blank to keep current)"
                : ""}
            </label>
            <TextInput
              type="password"
              id="credentialPassword"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              placeholder={
                editingCredential?.hasPassword ? "················" : ""
              }
              required={!editingCredential?.hasPassword}
              className="w-full"
            />
          </div>
        </div>
      )}
      {credentialType === "BearerToken" && (
        <div>
          <label
            htmlFor="credentialToken"
            className="mb-1.5 block text-sm text-foreground"
          >
            Token
            {editingCredential?.hasPassword
              ? " (leave blank to keep current)"
              : ""}
          </label>
          <TextInput
            type="password"
            id="credentialToken"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder={
              editingCredential?.hasPassword ? "················" : ""
            }
            required={!editingCredential?.hasPassword}
            autoComplete="off"
            className="w-full"
          />
        </div>
      )}
      {credentialType === "GoogleServiceAccount" && (
        <div>
          <label
            htmlFor="credentialServiceAccountKey"
            className="mb-1.5 block text-sm text-foreground"
          >
            Key
            {editingCredential?.hasPassword
              ? " (leave blank to keep current)"
              : ""}
          </label>
          <textarea
            id="credentialServiceAccountKey"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder={
              editingCredential?.hasPassword
                ? "················"
                : '{ "type": "service_account", ... }'
            }
            required={!editingCredential?.hasPassword}
            rows={3}
            autoComplete="off"
            spellCheck={false}
            className="w-full rounded-lg border border-muted bg-surface px-3 py-2 font-mono text-xs text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
          />
        </div>
      )}
      {credentialType === "UsernameKeyPair" && (
        <>
          <div className="flex items-end gap-3">
            <div className="min-w-0 basis-1/2">
              <label
                htmlFor="credentialUsernameKeyPair"
                className="mb-1.5 block text-sm text-foreground"
              >
                Username
              </label>
              <TextInput
                id="credentialUsernameKeyPair"
                value={username}
                onChange={(e) => onUsernameChange(e.target.value)}
                required
                className="w-full"
              />
            </div>
            <div className="min-w-0 basis-1/2">
              <label
                htmlFor="credentialPassphrase"
                className="mb-1.5 block text-sm text-foreground"
              >
                Passphrase (optional)
              </label>
              <TextInput
                type="password"
                id="credentialPassphrase"
                value={passphrase}
                onChange={(e) => onPassphraseChange(e.target.value)}
                placeholder={
                  editingCredential?.hasPassphrase ? "················" : ""
                }
                className="w-full"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="credentialPrivateKey"
              className="mb-1.5 block text-sm text-foreground"
            >
              Private key
            </label>
            <textarea
              id="credentialPrivateKey"
              value={privateKey}
              onChange={(e) => onPrivateKeyChange(e.target.value)}
              placeholder={
                editingCredential?.hasPrivateKey
                  ? "················"
                  : "-----BEGIN OPENSSH PRIVATE KEY-----"
              }
              required={!editingCredential?.hasPrivateKey}
              rows={3}
              className="w-full rounded-lg border border-muted bg-surface px-3 py-2 font-mono text-xs text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </div>
        </>
      )}
    </>
  );
}
